export const MC_RESPONSE_RECIPIENTS_KEY = "mc-response-recipients";
export const MC_RESPONSE_RECIPIENT_EVENT = "mc-response-recipient-change";

export const RECIPIENT_ROLE_OPTIONS = [
  { id: "site_manager", label: "Site Manager" },
  { id: "operations_manager", label: "Operations Manager" },
  { id: "owner", label: "Owner" },
  { id: "inventory_manager", label: "Inventory Manager" },
  { id: "finance", label: "Finance" },
  { id: "custom", label: "Custom" },
];

function now() {
  return new Date().toISOString();
}

function safeParse(value, fallback) {
  try {
    const parsed = JSON.parse(value || "null");
    return parsed ?? fallback;
  } catch (_) {
    return fallback;
  }
}

export function parseRecipientEmails(value) {
  const raw = Array.isArray(value) ? value.join("\n") : String(value || "");
  return Array.from(new Set(raw.split(/[\n,;]+/).map((item) => item.trim()).filter(Boolean)));
}

export function parseRecipientPhones(value) {
  const raw = Array.isArray(value) ? value.join("\n") : String(value || "");
  return Array.from(new Set(raw.split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter(Boolean)));
}

export const RECIPIENT_CHANNEL_OPTIONS = [
  { id: "workspace", label: "Workspace" },
  { id: "email", label: "Email" },
  { id: "whatsapp", label: "WhatsApp" },
];

export function normalizeRecipientRule(rule = {}) {
  const stamp = now();
  const siteId = rule.siteId || (String(rule.site || "").toLowerCase() === "all sites" ? "all" : "all");
  const emails = parseRecipientEmails(rule.emailText || rule.emails || []);
  const phones = parseRecipientPhones(rule.phoneText || rule.phones || []);
  const channels = Array.from(new Set((rule.channels || ["workspace", "email"]).filter(Boolean).map((item) => String(item).toLowerCase())));
  return {
    id: rule.id || `recipient-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    severity: String(rule.severity || "critical").toLowerCase(),
    siteId,
    siteName: rule.siteName || rule.site || (siteId === "all" ? "All Sites" : "Selected site"),
    roles: Array.from(new Set((rule.roles || []).filter(Boolean))),
    emails,
    emailText: emails.join("\n"),
    phones,
    phoneText: phones.join("\n"),
    channels: channels.length ? channels : ["workspace"],
    active: rule.active !== false,
    createdAt: rule.createdAt || stamp,
    updatedAt: stamp,
  };
}

export function createResponseRecipientRule(seed = {}) {
  return normalizeRecipientRule({
    severity: "critical",
    siteId: "all",
    siteName: "All Sites",
    roles: ["site_manager", "operations_manager", "owner"],
    emails: [],
    phones: [],
    channels: ["workspace", "email", "whatsapp"],
    active: true,
    ...seed,
  });
}

export function loadResponseRecipientRules() {
  if (typeof window === "undefined") return [];
  const raw = window.localStorage?.getItem(MC_RESPONSE_RECIPIENTS_KEY)
    || window.sessionStorage?.getItem(MC_RESPONSE_RECIPIENTS_KEY);
  const parsed = safeParse(raw, window.__mcResponseRecipientRulesMemory || []);
  return uniqueRecipientRules(parsed).map(normalizeRecipientRule);
}

function removeSafeMorpheusCacheKeys() {
  if (typeof window === "undefined") return { removed: 0 };
  const shouldRemove = (key = "") => {
    const clean = String(key || "");
    return /^mc-session-engine[:\-]/i.test(clean)
      || /^mc-live-update[:\-]/i.test(clean)
      || /^mc-link-inventory-snapshot(?::|$)/i.test(clean)
      || /^mc-import-active(?::|$)/i.test(clean)
      || /^mc-active-import-lock(?::|$)/i.test(clean)
      || /^mc-latest-import-lock(?::|$)/i.test(clean)
      || clean === "mc-activity-log"
      || clean === "mc-warm-cache-state";
  };
  let removed = 0;
  [window.localStorage, window.sessionStorage].forEach((storage) => {
    if (!storage) return;
    const keys = [];
    try {
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i) || "";
        if (shouldRemove(key)) keys.push(key);
      }
      keys.forEach((key) => {
        try { storage.removeItem(key); removed += 1; } catch (_) {}
      });
    } catch (_) {}
  });
  return { removed };
}

function compactRecipientRule(rule = {}) {
  const normalized = normalizeRecipientRule(rule);
  return {
    id: normalized.id,
    severity: normalized.severity,
    siteId: normalized.siteId,
    siteName: normalized.siteName,
    roles: normalized.roles,
    emails: normalized.emails,
    phones: normalized.phones,
    channels: normalized.channels,
    active: normalized.active,
    createdAt: normalized.createdAt,
    updatedAt: normalized.updatedAt,
  };
}

function uniqueRecipientRules(rules = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(rules) ? rules : []).forEach((rule) => {
    const normalized = compactRecipientRule(rule);
    const signature = [
      normalized.id,
      normalized.severity,
      normalized.siteId,
      (normalized.roles || []).join("|"),
      (normalized.emails || []).join("|"),
      (normalized.phones || []).join("|"),
      (normalized.channels || []).join("|"),
    ].join("::");
    if (seen.has(signature)) return;
    seen.add(signature);
    out.push(normalized);
  });
  return out;
}

export function saveResponseRecipientRules(rules = []) {
  const normalized = uniqueRecipientRules(rules);
  if (typeof window !== "undefined") {
    const payload = JSON.stringify(normalized);
    try {
      window.localStorage?.setItem(MC_RESPONSE_RECIPIENTS_KEY, payload);
    } catch (firstError) {
      removeSafeMorpheusCacheKeys();
      try {
        window.localStorage?.setItem(MC_RESPONSE_RECIPIENTS_KEY, payload);
      } catch (secondError) {
        try {
          window.sessionStorage?.setItem(MC_RESPONSE_RECIPIENTS_KEY, payload);
        } catch (_) {}
        window.__mcResponseRecipientRulesMemory = normalized;
        const error = new Error("Morpheus could not save recipient rules because browser storage is full. Safe cache was cleared; try saving again.");
        error.code = "MC_RECIPIENT_STORAGE_FULL";
        error.cause = secondError || firstError;
        throw error;
      }
    }
    window.dispatchEvent(new CustomEvent(MC_RESPONSE_RECIPIENT_EVENT));
  }
  return normalized;
}

export function updateResponseRecipientRule(ruleId, patch = {}) {
  const current = loadResponseRecipientRules();
  const next = current.map((rule) => rule.id === ruleId ? normalizeRecipientRule({ ...rule, ...patch, id: rule.id, createdAt: rule.createdAt }) : rule);
  return saveResponseRecipientRules(next);
}

export function deleteResponseRecipientRule(ruleId) {
  return saveResponseRecipientRules(loadResponseRecipientRules().filter((rule) => rule.id !== ruleId));
}

export function resolveResponseRecipients({ siteId = "all", severity = "critical" } = {}) {
  const sev = String(severity || "critical").toLowerCase();
  const site = String(siteId || "all");
  const matched = loadResponseRecipientRules().filter((rule) => {
    if (rule.active === false) return false;
    const severityOk = String(rule.severity || "critical").toLowerCase() === sev;
    const siteOk = rule.siteId === "all" || rule.siteId === site;
    return severityOk && siteOk;
  });
  return {
    rules: matched,
    roles: Array.from(new Set(matched.flatMap((rule) => rule.roles || []))),
    emails: Array.from(new Set(matched.flatMap((rule) => rule.emails || []))),
    phones: Array.from(new Set(matched.flatMap((rule) => rule.phones || []))),
    channels: Array.from(new Set(matched.flatMap((rule) => rule.channels || []))),
  };
}
